import React, { useEffect, useRef, useState } from 'react'
import NextImage from 'next/image'
import styles from './styles.module.scss'
import {
  documentToReactComponents,
  Options
} from '@contentful/rich-text-react-renderer'
import { BLOCKS, Document } from '@contentful/rich-text-types'
import { ButtonLink } from '@components/ui/Button'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

function Paragraph({ text }: { text: string }) {
  return text === '' ? null : <p>{text}</p>
}

function RenderText(node: Document) {
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.DESKTOP,
    initialState: true
  })
  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: node => {
        return node.content.map((childNode, index) => {
          if (childNode.nodeType === 'text') {
            const { value } = childNode || {}
            return <Paragraph text={value} key={`${value}-${index}`} />
          }
          if (childNode.nodeType === 'embedded-entry-inline') {
            const { buttonStyle, text, uri } =
              childNode.data.target.fields || {}
            return (
              <ButtonLink
                key={`${uri}`}
                href={uri}
                variant={isMobile ? 'primary_outline' : buttonStyle}
                classes={styles.FeatureCards__contentButton}
              >
                {text}
              </ButtonLink>
            )
          }
          return null // Ensure a fallback for other node types
        })
      },
      [BLOCKS.EMBEDDED_ASSET]: node => {
        const { url, details, contentType } = node.data.target.fields.file

        if (contentType === 'image/svg+xml') {
          return (
            <div
              className={styles.svgIcon}
              key={`${url}`}
              style={
                {
                  maskImage: `url(${url})`,
                  WebkitMaskImage: `url(${url})`,
                  '--svgIcon-width': `${details.image.width}px`,
                  '--svgIcon-height': `${details.image.height}px`
                } as React.CSSProperties
              }
            />
          )
        }

        return (
          <NextImage
            src={url}
            alt={node.data.target.fields.title}
            width={details.size.width}
            height={details.size.height}
            key={`${url}`}
          />
        )
      }
    }
  }
  return documentToReactComponents(node, options)
}

type ImageCopySectionCopyProps = {
  copy: Document[]
  children?: React.ReactNode
}

export default function ImageCopySectionCopy({
  copy,
  children
}: ImageCopySectionCopyProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    function checkMaskVisibility() {
      if (!el) return

      const isOverflowing = el.scrollHeight > el.clientHeight
      const isAtBottom =
        Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 1

      if (isOverflowing && !isAtBottom) {
        setIsOverflowing(true)
      } else {
        setIsOverflowing(false)
      }
    }

    // Initial check
    checkMaskVisibility()

    // Listen to scroll
    el.addEventListener('scroll', checkMaskVisibility)

    // Listen to window resize
    window.addEventListener('resize', checkMaskVisibility)

    // MutationObserver for dynamic content changes
    const observer = new MutationObserver(checkMaskVisibility)
    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true
    })

    return () => {
      el.removeEventListener('scroll', checkMaskVisibility)
      window.removeEventListener('resize', checkMaskVisibility)
      observer.disconnect()
    }
  }, [])

  return (
    <div className={styles.ImageCopySectionCopy}>
      <div
        className={`${styles.ImageCopySectionCopy__wrapper} ${
          isOverflowing ? styles.overflowing : ''
        }`}
        ref={containerRef}
      >
        {copy.map(node => RenderText(node))}
      </div>
      {children}
    </div>
  )
}
